import React, { forwardRef, useCallback, useRef } from "react";
import { PaperClipOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import XAdkSender from "@/components/XAdkSender";
import { useChatSenderState } from "../context/ChatSenderStateContext";
import { useDefaultLayoutContext } from "../context/DefaultLayoutContext";
import { useChatActions } from "../context/ChatActionContext";
import type {
  ActionsComponents,
  SlotRenderFunction,
  XAdkSenderProps,
  XAdkSenderHandle,
} from "@/types";
import { useStyles } from "./styles";

/**
 * XAdkProvider.Sender - 输入框组件
 *
 * 从 Context 获取聊天动作并提供输入界面
 * 基于 XAdkSender 组件实现
 *
 * @example
 * ```tsx
 * <XAdkProvider url="..." token="...">
 *   <XAdkProvider.Sender
 *     clearBtnShow={true}
 *     allowUpload
 *     uploadRequest={myUploadHandler}
 *   />
 * </XAdkProvider>
 * ```
 */
export interface XAdkSenderCompoundProps extends Partial<
  Omit<XAdkSenderProps, "loading" | "onSubmit" | "onStop" | "onClear">
> {
  /** 是否显示内置上传按钮；未传时依次继承 DefaultLayout 与 Provider 配置。 */
  allowUpload?: boolean;
  /** 额外的样式 */
  style?: React.CSSProperties;
  /** 额外的类名 */
  className?: string;
}

const Sender = forwardRef<XAdkSenderHandle, XAdkSenderCompoundProps>(
  (
    { allowUpload, uploadRequest: customUploadRequest, footerLeft, ...props },
    ref,
  ) => {
    const {
      loading,
      uploadRequest: providerUploadRequest,
      allowUpload: providerAllowUpload,
      senderValue,
      setSenderValue,
    } = useChatSenderState();
    const layout = useDefaultLayoutContext();
    const { chat, stopChat, clearChat } = useChatActions();
    const styles = useStyles();
    const senderRef = useRef<XAdkSenderHandle | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const mergedAllowUpload =
      allowUpload ?? layout?.allowUpload ?? providerAllowUpload;
    const uploadRequest = customUploadRequest ?? providerUploadRequest;

    // 同时维护业务 ref 与 DefaultLayout 的拖拽文件注入目标。
    const setSenderRef = useCallback(
      (sender: XAdkSenderHandle | null) => {
        senderRef.current = sender;
        layout?.registerSender?.(sender);
        if (typeof ref === "function") ref(sender);
        else if (ref) ref.current = sender;
      },
      [layout, ref],
    );

    const handleFileChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files?.length) senderRef.current?.addFiles(Array.from(files));
        event.target.value = "";
      },
      [],
    );

    const renderCustomFooterLeft = useCallback(
      (oriNode: React.ReactNode, info: { components: ActionsComponents }) => {
        if (footerLeft === false || footerLeft === undefined) return null;
        return typeof footerLeft === "function"
          ? footerLeft(oriNode, info)
          : footerLeft;
      },
      [footerLeft],
    );

    const mergedFooterLeft: SlotRenderFunction | React.ReactNode =
      mergedAllowUpload
        ? (oriNode, info) => (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={props.allowedFileTypes?.join(",")}
                onChange={handleFileChange}
              />
              <Tooltip title="上传文件">
                <button
                  type="button"
                  className={styles.uploadButton}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="上传文件"
                >
                  <PaperClipOutlined />
                </button>
              </Tooltip>
              {renderCustomFooterLeft(oriNode, info)}
            </>
          )
        : footerLeft;

    return (
      <XAdkSender
        ref={setSenderRef}
        {...props}
        loading={loading}
        value={senderValue}
        onChange={setSenderValue}
        onSubmit={chat}
        onStop={stopChat}
        onClear={clearChat}
        uploadRequest={uploadRequest}
        footerLeft={mergedFooterLeft}
      />
    );
  },
);

Sender.displayName = "XAdkProvider.Sender";

export default Sender;
